import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Heart, MessageCircle, Search, User, LogOut, Inbox, Filter, Bell, Headphones, Coins, ChevronRight, MapPin } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { useNotifications } from '../../context/NotificationContext';
import API_URL from '../../utils/apiUrl';
import { t } from '../../i18n/t';

// Brand fallback = the site's own domain, never the word "Dating".
const hostBrand = (() => {
  const h = (window.location.hostname || '').replace(/^www\./, '').split('.')[0];
  return h ? h.charAt(0).toUpperCase() + h.slice(1) : '';
})();

// Talk sites are chat sites: no dating heart.
const isTalk = process.env.REACT_APP_MODE === 'talk';
const BrandMark = isTalk ? MessageCircle : Heart;

const DatingDiscover = () => {
  const navigate = useNavigate();
  const { user, siteId, logout, getAuthHeader } = useAuth();
  const { unreadCount } = useNotifications();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState(user?.looking_for || 'all');
  const [siteName, setSiteName] = useState('');
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/credits/balance`, { headers: getAuthHeader() })
      .then(r => setCredits(r.data?.credits ?? 0))
      .catch(() => setCredits(null));
  }, []);

  useEffect(() => {
    const fetchSiteName = async () => {
      const domainName = window.location.hostname.replace(/^www\./, '').split('.')[0];
      const displayName = domainName.charAt(0).toUpperCase() + domainName.slice(1);
      try {
        const res = await axios.get(`${API_URL}/api/sites/config/${siteId}`);
        const name = res.data?.name || '';
        setSiteName(name && name.toLowerCase() !== 'sex lek' ? name : displayName);
      } catch (e) {
        setSiteName(displayName);
      }
    };
    fetchSiteName();
  }, [siteId]);

  useEffect(() => {
    fetchProfiles();
  }, [siteId, genderFilter]);

  const fetchProfiles = async () => {
    try {
      const isStandalone = process.env.REACT_APP_MODE === 'standalone';
      let url;
      if (isStandalone) {
        url = `${API_URL}/api/standalone/all-users?exclude_self=${user?.id || ''}`;
      } else {
        url = `${API_URL}/api/profiles/discover/${siteId}?exclude_self=${user?.id || ''}`;
      }
      if (genderFilter && genderFilter !== 'all') {
        url += `&gender=${genderFilter}`;
      }
      const response = await axios.get(url, { headers: getAuthHeader() });
      setProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
    }
    setLoading(false);
  };

  const filteredProfiles = profiles.filter(profile =>
    profile.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.bio?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const defaultAvatars = [
    'https://images.unsplash.com/photo-1593704345154-791a1430ac8f?w=400&h=500&fit=crop',
    'https://images.unsplash.com/photo-1618676306664-092ba59aac0d?w=400&h=500&fit=crop',
    'https://images.unsplash.com/photo-1614477323662-66300f4e033c?w=400&h=500&fit=crop',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop'
  ];

  return (
    <div className="min-h-screen dating-bg font-dating-body">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-rose-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <BrandMark className="h-7 w-7 text-rose-500 fill-rose-500" />
            <span className="font-dating-heading text-xl font-bold text-slate-800">
              {siteName || hostBrand}
            </span>
          </div>
          
          {/* Search + Filter */}
          <div className="hidden md:flex items-center max-w-lg flex-1 mx-8 gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder={t('searchProfiles')}
                className="pl-10 bg-slate-50 border-slate-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-36 bg-slate-50 border-slate-200" data-testid="gender-filter">
                <Filter className="h-4 w-4 mr-1 text-slate-400" />
                <SelectValue placeholder={t('all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="female">{t('women')}</SelectItem>
                <SelectItem value="male">{t('men')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Nav */}
          <nav className="flex items-center gap-2">
            {isTalk && (
              <Button
                onClick={() => navigate('/credits')}
                className="msg-cta group h-10 rounded-full px-3 sm:px-5 font-semibold hover:bg-transparent"
                data-testid="buy-messages-btn"
              >
                <Coins className="h-4 w-4 sm:mr-2 transition-transform group-hover:rotate-12" />
                <span className="hidden sm:inline">{t('buyMessagesHere')}</span>
                {credits !== null && (
                  <span className="ml-2 rounded-full bg-white/25 px-2 py-0.5 text-xs" data-testid="header-credits">
                    {credits}
                  </span>
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/chat')}
              className="relative"
              data-testid="nav-chat"
            >
              <Inbox className="h-5 w-5" />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold px-1 animate-pulse"
                  data-testid="unread-badge"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/support')}
              data-testid="nav-support"
            >
              <Headphones className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
              data-testid="nav-profile"
            >
              <User className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="nav-logout"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </nav>
        </div>
      </header>

      {/* Mobile Search + Filter */}
      <div className="md:hidden px-4 py-3 bg-white border-b border-rose-100 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder={t('searchProfiles')}
            className="pl-10 bg-slate-50 border-slate-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={genderFilter} onValueChange={setGenderFilter}>
          <SelectTrigger className="w-28 bg-slate-50 border-slate-200" data-testid="gender-filter-mobile">
            <SelectValue placeholder={t('all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="female">{t('women')}</SelectItem>
            <SelectItem value="male">{t('men')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            {/* Buy-messages banner — impossible to miss, right above the profiles */}
            {isTalk && (
            <div
              className="mb-6 flex flex-col gap-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-50/60 to-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
              data-testid="buy-messages-banner"
            >
              <div className="flex items-center gap-4">
                <div className="msg-cta flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
                  <Coins className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-dating-heading text-lg font-semibold text-slate-800" data-testid="banner-balance">
                    {credits === null
                      ? t('buyMessagesHere')
                      : credits === 0
                        ? t('outOfMessages')
                        : `${credits} ${t('messagesLeft')}`}
                  </p>
                  <p className="text-sm text-slate-500">
                    {credits !== null && credits > 0 && credits <= 5
                      ? t('lowOnMessages')
                      : credits === 0
                        ? t('needCredits')
                        : t('readyToChat')}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/credits')}
                className="msg-cta h-12 w-full rounded-full px-8 text-base font-semibold hover:bg-transparent sm:w-auto"
                data-testid="buy-messages-banner-btn"
              >
                {t('buyMessagesHere')}
                <ChevronRight className="ml-1 h-5 w-5" />
              </Button>
            </div>
            )}

            <h1 className="font-dating-heading text-2xl sm:text-3xl font-semibold text-slate-800 mb-6">
              {process.env.REACT_APP_MODE === 'talk' ? t('profilesTitle') : t('discoverPeople')}
            </h1>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <div className="text-center py-12">
            <BrandMark className="h-12 w-12 text-rose-200 mx-auto mb-4" />
            <p className="text-slate-500">{t('noProfilesYet')}</p>
            <p className="text-sm text-slate-400 mt-2">{t('checkBackSoon')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="profiles-grid">
            {filteredProfiles.map((profile, index) => (
              <div
                key={profile.id}
                className="profile-card animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => navigate(`/profile/${profile.id}`)}
                data-testid={`profile-card-${profile.id}`}
              >
                <img
                  src={(() => {
                    const av = profile.main_avatar || defaultAvatars[index % defaultAvatars.length];
                    return av.startsWith('http') ? av : `${API_URL}${av}`;
                  })()}
                  alt={profile.username}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultAvatars[index % defaultAvatars.length];
                  }}
                />
                <div className="profile-card-overlay" />
                {profile.is_online && (
                  <span
                    className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-emerald-500/95 px-2 py-0.5 text-[11px] font-semibold text-white shadow"
                    data-testid={`profile-presence-${profile.id}`}
                  >
                    <span className="live-dot h-1.5 w-1.5 rounded-full bg-white" />
                    {t('online')}
                  </span>
                )}
                <div className="profile-card-info">
                  <h3 className="font-semibold text-lg">
                    {profile.username}{profile.age ? `, ${profile.age}` : ''}
                  </h3>
                  {profile.display_city && (
                    <p className="flex items-center gap-1 text-sm text-white/90 mt-0.5"
                       data-testid={`profile-city-${profile.id}`}>
                      <MapPin className="h-3.5 w-3.5" />
                      {profile.display_city}
                    </p>
                  )}
                  {profile.bio && (
                    <p className="text-sm text-white/80 line-clamp-2 mt-1">{profile.bio}</p>
                  )}
                </div>
                
                {/* Quick Action */}
                <button
                  className="absolute top-3 right-3 p-2 rounded-full bg-white/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/chat/${profile.id}`);
                  }}
                  data-testid={`chat-btn-${profile.id}`}
                >
                  <MessageCircle className="h-5 w-5 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DatingDiscover;
